import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { labelKey: "nav.home", path: "/" },
    { labelKey: "nav.mission", path: "/mission" },
    { labelKey: "nav.projects", path: "/projects" },
    { labelKey: "nav.stories", path: "/stories" },
    { labelKey: "nav.volunteer", path: "/volunteer" },
    { labelKey: "nav.donate", path: "/donate" },
    { labelKey: "nav.contact", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <nav
      className={`w-full z-40 transition-all duration-300 ${
        scrolled || !isHome
          ? "fixed top-0 bg-white border-b border-gray-100"
          : "absolute top-0 bg-transparent"
      }`}
      style={{ top: isHome && scrolled ? "0" : "36px" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1B2838] text-white font-bold text-sm">
            CAF
          </div>
          <div className="hidden sm:block">
            <div
              className={`font-bold text-sm leading-tight ${
                scrolled || !isHome ? "text-[#1B2838]" : "text-white"
              }`}
            >
              Canada Afghanistan
            </div>
            <div className="text-xs text-[#4ECDC4]">Foundation</div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                location.pathname === link.path
                  ? "text-[#4ECDC4] bg-[#4ECDC4]/10"
                  : scrolled || !isHome
                  ? "text-gray-700 hover:text-[#1B2838] hover:bg-gray-50"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <Link
            to="/donate"
            className="ml-3 px-5 py-2 bg-[#4ECDC4] text-[#1B2838] rounded-full text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-[#3dbdb5] transition-colors"
          >
            {t("nav.donateNow")}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
            scrolled || !isHome ? "text-gray-700" : "text-white"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                  location.pathname === link.path
                    ? "text-[#4ECDC4] bg-[#4ECDC4]/10"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <Link
              to="/donate"
              className="mt-2 px-5 py-3 bg-[#4ECDC4] text-[#1B2838] rounded-full text-sm font-bold text-center cursor-pointer"
            >
              {t("nav.donateNow")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
